/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, jsx-a11y/alt-text */
import React, { Children, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "../../assets/images/pebble-logo.png";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import DashboardContent from "../../components/MPS/DashboardContent";
import EventsContent from "../../components/MPS/EventsContent";
import SettingsContent from "../../components/MPS/SettingsContent";
import AnnouncementsTab from "../../components/MPS/Announcements/AnnouncementsTab";
import { useAuth } from "../../context/AuthContext";
import LegalModal from "../../components/ui/LegalModal";

import dashboard_home_icon from "../../assets/images/dashboard_icons/dashboard_home_icon.svg";
import dashboard_events_icon from "../../assets/images/dashboard_icons/dashboard_events_icon.svg";
import dashboard_settings_icon from "../../assets/images/dashboard_icons/dashboard_settings_icon.svg";
import dashboard_mps_icon from "../../assets/images/dashboard_icons/dashboard_marketplacesellers_icon.svg";

import breadcrumb from "../../assets/images/Nav/chevron-forward.svg";
import dashboard_home_icon_active from "../../assets/images/dashboard_icons/dashboard_home_white_icon.svg";
import dashboard_events_icon_active from "../../assets/images/dashboard_icons/dashboard_events_white_icon.svg";
import dashboard_settings_icon_active from "../../assets/images/dashboard_icons/dashboard_settings_white_icon.svg";
import dashboard_mps_icon_active from "../../assets/images/dashboard_icons/dashboard_marketplacesellers_white_icon.svg";
import dashboard_receipts_icon from "../../assets/images/dashboard_icons/dashboard_receipts_icon.svg";
import dashboard_receipts_white_icon from "../../assets/images/dashboard_icons/dashboard_receipts_white_icon.svg";
import { HelpCircle, BookOpen, Store, Megaphone } from "lucide-react";
import WelcomeModal from "../../components/Onboarding/WelcomeModal";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import DarkModeToggle from "../../components/ui/DarkModeToggle";
import NotificationBell from "../../components/ui/NotificationBell";
import { displayMPSProfile } from "../../firebaseFunctions/displayMPSProfile/displayMPSProfile";

const MPSDashboard = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeContent, setActiveContent] = useState("dashboard");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const navItems = [
    {
      id: "dashboard",
      icon: dashboard_home_icon,
      activeIcon: dashboard_home_icon_active,
      textKey: "nav.dashboard",
      path: "/mps/dashboard",
    },
    {
      id: "events",
      icon: dashboard_events_icon,
      activeIcon: dashboard_events_icon_active,
      textKey: "nav.events",
      paths: [
        "/mps/events",
        "/mps/view-event-details/",
        "/mps/event-registration/",
      ],
    },
    {
      id: "receipts",
      icon: dashboard_receipts_icon,
      activeIcon: dashboard_receipts_white_icon,
      textKey: "nav.receipts",
      path: "/mps/receipts",
    },
    {
      id: "announcements",
      icon: null, // lucide icon used directly
      textKey: "nav.announcements",
      path: "/mps/announcements",
    },
    {
      id: "marketplace",
      icon: dashboard_mps_icon,
      activeIcon: dashboard_mps_icon_active,
      textKey: "nav.marketplaceVendors",
      path: "/mps/marketplace",
    },
    {
      id: "settings",
      icon: dashboard_settings_icon,
      activeIcon: dashboard_settings_icon_active,
      textKey: "nav.settings",
      paths: ["/mps/settings"],
    },
  ];

  // Show first-time profile setup modal + track incomplete banner (once per session for modal)
  useEffect(() => {
    if (!user?.uid) return;
    const checkProfile = async () => {
      try {
        const profile = await displayMPSProfile(user.uid);
        const required = ["businessName", "typeOfBusiness", "email", "phoneNumber"];
        const incomplete = !profile || required.some(f => !profile[f]);
        setProfileIncomplete(incomplete);
        if (incomplete && !sessionStorage.getItem("mpsSetupModalSeen")) {
          setShowSetupModal(true);
        }
      } catch {
        // Non-blocking
      }
    };
    checkProfile();
  }, [user?.uid]);

  useEffect(() => {
    const currentPath = location.pathname;

    if (currentPath.startsWith("/mps/tutorials")) {
      setActiveContent("tutorials");
      return;
    }
    if (currentPath.startsWith("/mps/support")) {
      setActiveContent("support");
      return;
    }
    if (currentPath.startsWith("/mps/announcements")) {
      setActiveContent("announcements");
      return;
    }

    const currentNavItem = navItems.find((item) => {
      if (item.paths) {
        return item.paths.some((path) => currentPath.startsWith(path));
      }
      return item.path === currentPath;
    });
    if (currentNavItem) {
      setActiveContent(currentNavItem.id);
    } else {
      setActiveContent("dashboard");
    }
  }, [location.pathname, navItems]);

  const dismissSetupModal = () => {
    sessionStorage.setItem("mpsSetupModalSeen", "true");
    setShowSetupModal(false);
  };

  const goToProfileSetup = () => {
    dismissSetupModal();
    navigate("/mps/settings");
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <WelcomeModal />

      {/* First-time profile setup modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-8 shadow-xl text-center">
            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="w-8 h-8 text-primary-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to Pebble!</h2>
            <p className="text-sm text-gray-500 mb-6">
              Set up your vendor profile so event organizers can learn about your business. It only takes a minute.
            </p>
            <button
              onClick={goToProfileSetup}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors mb-3 text-sm"
            >
              Set Up My Profile
            </button>
            <button
              onClick={dismissSetupModal}
              className="w-full py-2 px-4 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              I&apos;ll do this later
            </button>
          </div>
        </div>
      )}
        {/* Mobile backdrop overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-52 flex-shrink-0 overflow-y-auto transition-transform duration-300 ease-in-out md:static md:translate-x-0 ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            borderRight: "1px solid var(--Grey-300, #D0D5DD)",
            background: "var(--White, #FEFEFE)",
            boxShadow:
              "0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)",
          }}
        >
          <div className="p-3 flex justify-center mt-4">
            <Link to="/mps/dashboard">
              <img src={logo} alt="Pebble" className="h-10 w-auto" />
            </Link>
          </div>
          <nav className="mt-4 pb-4">
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.analyze")}
              </h2>
              {navItems
                .filter((item) => item.id === "dashboard")
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.paths ? item.paths[0] : item.path);
                      setIsSidebarOpen(false);
                    }}
                    className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                      activeContent === item.id
                        ? "bg-primary-500 text-white"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <img
                      src={
                        activeContent === item.id ? item.activeIcon : item.icon
                      }
                      alt={t(item.textKey)}
                      className="h-4 w-4 mr-2.5 flex-shrink-0"
                    />
                    {t(item.textKey)}
                  </button>
                ))}
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.manage")}
              </h2>
              {navItems
                .filter(
                  (item) => item.id === "events" || item.id === "receipts",
                )
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.paths ? item.paths[0] : item.path);
                      setIsSidebarOpen(false);
                    }}
                    className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                      activeContent === item.id
                        ? "bg-primary-500 text-white"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <img
                      src={
                        activeContent === item.id ? item.activeIcon : item.icon
                      }
                      alt={t(item.textKey)}
                      className="h-4 w-4 mr-2.5 flex-shrink-0"
                    />
                    {t(item.textKey)}
                  </button>
                ))}
              {/* Announcements */}
              <button
                onClick={() => { navigate("/mps/announcements"); setIsSidebarOpen(false); setUnreadAnnouncements(0); }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "announcements"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Megaphone className={`h-4 w-4 mr-2.5 flex-shrink-0 ${activeContent === "announcements" ? "text-white" : "text-gray-500"}`} />
                Announcements
                {unreadAnnouncements > 0 && activeContent !== "announcements" && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 bg-primary-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {unreadAnnouncements > 9 ? "9+" : unreadAnnouncements}
                  </span>
                )}
              </button>
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1 flex items-center gap-1.5">
                {t("sidebar.pro")}
                <span className="px-1.5 py-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[9px] font-medium rounded-full">
                  {t("sidebar.comingSoon")}
                </span>
              </h2>
              <button
                onClick={() => { navigate("/mps/tutorials"); setIsSidebarOpen(false); }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "tutorials"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BookOpen
                  className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                    activeContent === "tutorials"
                      ? "text-white"
                      : "text-gray-500"
                  }`}
                />
                {t("nav.tutorials")}
              </button>
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                Resources
              </h2>
              <button
                onClick={() => { navigate("/mps/support"); setIsSidebarOpen(false); }}
                className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                  activeContent === "support"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <HelpCircle
                  className={`h-4 w-4 mr-2.5 flex-shrink-0 ${
                    activeContent === "support" ? "text-white" : "text-gray-500"
                  }`}
                />
                {t("nav.support")}
              </button>
            </div>
            <div className="mb-3">
              <h2 className="text-gray-400 text-xs font-medium uppercase tracking-wider px-4 mb-1">
                {t("sidebar.preference")}
              </h2>
              {navItems
                .filter((item) => item.id === "settings")
                .map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.paths ? item.paths[0] : item.path);
                      setIsSidebarOpen(false);
                    }}
                    className={`flex items-center w-full h-10 px-4 py-2 text-sm text-left ${
                      activeContent === item.id
                        ? "bg-primary-500 text-white"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <img
                      src={
                        activeContent === item.id ? item.activeIcon : item.icon
                      }
                      alt={t(item.textKey)}
                      className="h-4 w-4 mr-2.5 flex-shrink-0"
                    />
                    {t(item.textKey)}
                  </button>
                ))}
            </div>
          </nav>
        </aside>
        <div className="flex-1 flex flex-col overflow-auto">
          <header className="bg-white shadow-sm border border-gray-300 top-0">
            <div className="mx-auto h-[80px] justify-between content-center px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <button
                    onClick={toggleSidebar}
                    className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                  >
                    <span className="sr-only">{t("sidebar.openSidebar")}</span>
                    <svg
                      className="h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                  </button>
                  {/* Mobile: show logo */}
                  <img
                    src={logo}
                    alt="Pebble"
                    className="h-8 w-auto ml-2 md:hidden"
                  />
                  {/* Desktop: show breadcrumb title */}
                  <h2 className="ml-3 text-xl font-medium text-gray-900 leading-6 hidden md:block">
                    {activeContent === "dashboard" ? (
                      t("nav.dashboard")
                    ) : (
                      <>
                        {t("nav.dashboard")}
                        <img
                          src={breadcrumb}
                          className="mx-2 -mt-1 h-6 w-6 inline"
                          alt={t("nav.dashboard")}
                        />
                        <strong className="font-semibold">
                          {t(
                            navItems.find((item) => item.id === activeContent)
                              ?.textKey,
                          )}
                        </strong>
                      </>
                    )}
                  </h2>
                </div>
                <div className="flex items-center">
                  <div className="ml-3 flex flex-row items-center gap-4 mr-2 sm:mr-10 pr-2 sm:pr-6">
                    <DarkModeToggle variant="icon" />
                    <LanguageSwitcher />
                    <NotificationBell userId={user?.uid} recipientType="mps" />
                    <div onClick={signOut}>
                      <button
                        className="flex items-center max-w-xs rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 hover:text-[#B84141] transition-colors"
                        id="user-menu"
                        aria-haspopup="true"
                      >
                        <span className="sr-only">
                          {t("navigation.logout")}
                        </span>
                        <p>{t("navigation.logout")}</p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="flex-1 mx-2 my-2 sm:mx-4 sm:my-4 lg:mx-6 lg:my-6 bg-white rounded-lg shadow-md overflow-y-auto">
            {profileIncomplete && (
              <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-7 w-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 text-xs font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-amber-900">Complete your vendor profile</p>
                    <p className="text-xs text-amber-700 mt-0.5">Add your business name, type, email, and phone to start applying to events.</p>
                  </div>
                </div>
                <button
                  onClick={() => { setProfileIncomplete(false); navigate("/mps/settings"); }}
                  className="flex-shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 transition-colors"
                >
                  Complete Profile
                </button>
              </div>
            )}
            {children}
          </main>

          <footer className="w-full">
            <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
              <p className="text-center text-xs text-gray-400">
                {t("footer.copyright")} ·{" "}
                <button
                  onClick={() => setLegalModal({ isOpen: true, type: "terms" })}
                  className="hover:text-gray-600"
                >
                  {t("footer.terms")}
                </button>{" "}
                ·{" "}
                <button
                  onClick={() =>
                    setLegalModal({ isOpen: true, type: "privacy" })
                  }
                  className="hover:text-gray-600"
                >
                  {t("footer.privacyPolicy")}
                </button>
              </p>
            </div>
          </footer>

          <LegalModal
            isOpen={legalModal.isOpen}
            onClose={() => setLegalModal({ isOpen: false, type: null })}
            type={legalModal.type}
          />
        </div>
    </div>
  );
};
export default MPSDashboard;
